import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { apiClient, BulkOperationStatusResponse } from '../services/api';

interface BulkOperationModalProps {
    bulkOperationId: string;
    onClose: () => void;
}

const BulkOperationModal: React.FC<BulkOperationModalProps> = ({ bulkOperationId, onClose }) => {
    const [response, setResponse] = useState<BulkOperationStatusResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchOperation = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const result = await apiClient.getBulkOperationStatus(bulkOperationId);
                setResponse(result);
            } catch (err: any) {
                setError(err.message || 'Failed to load operation details');
            } finally {
                setIsLoading(false);
            }
        };

        fetchOperation();
    }, [bulkOperationId]);

    const operation = response?.data;

    const getStatusIcon = () => {
        if (!operation) return null;

        switch (operation.status) {
            case 'completed':
                return <CheckCircle className="w-6 h-6 text-green-600" />;
            case 'failed':
                return <AlertCircle className="w-6 h-6 text-red-600" />;
            case 'running':
            case 'pending':
                return <Clock className="w-6 h-6 text-blue-600" />;
            default:
                return null;
        }
    };

    const getStatusColor = () => {
        if (!operation) return 'gray';

        switch (operation.status) {
            case 'completed':
                return 'green';
            case 'failed':
                return 'red';
            case 'running':
            case 'pending':
                return 'blue';
            default:
                return 'gray';
        }
    };

    const formatDuration = (startedAt: Date | string | null, completedAt?: Date | string | null) => {
        if (!startedAt) return 'N/A';
        const start = new Date(startedAt);
        const end = completedAt ? new Date(completedAt) : new Date();
        const durationMs = end.getTime() - start.getTime();
        const minutes = Math.floor(durationMs / 60000);
        const seconds = Math.floor((durationMs % 60000) / 1000);

        if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    };

    const formatTimestamp = (timestamp: Date | string | null) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp);
        return date.toLocaleString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Automated Check Details</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-red-800">{error}</p>
                        </div>
                    ) : operation ? (
                        <div className="space-y-6">
                            {/* Status Banner */}
                            <div className={`bg-${getStatusColor()}-50 border border-${getStatusColor()}-200 rounded-lg p-4 flex items-center gap-3`}>
                                {getStatusIcon()}
                                <div>
                                    <p className={`font-semibold text-${getStatusColor()}-900 capitalize`}>{operation.status}</p>
                                    <p className={`text-sm text-${getStatusColor()}-700`}>
                                        {operation.status === 'completed' && 'All licence checks have been processed'}
                                        {operation.status === 'failed' && 'The automated check encountered errors'}
                                        {operation.status === 'running' && 'Checks are currently in progress'}
                                        {operation.status === 'pending' && 'Checks are queued and will begin shortly'}
                                    </p>
                                </div>
                            </div>

                            {/* Statistics */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <p className="text-sm text-gray-600 mb-1">Total Checks</p>
                                    <p className="text-2xl font-bold text-gray-900">{operation.totalItems}</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <p className="text-sm text-green-700 mb-1">Successful</p>
                                    <p className="text-2xl font-bold text-green-700">{operation.successCount}</p>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                    <p className="text-sm text-red-700 mb-1">Failed</p>
                                    <p className="text-2xl font-bold text-red-700">{operation.failureCount}</p>
                                </div>
                            </div>

                            {/* High Risk Alert - show if available in resultSummary */}
                            {operation.resultSummary?.highRiskDrivers > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertCircle className="w-5 h-5 text-red-600" />
                                        <p className="font-semibold text-red-900">High-Risk Drivers Detected</p>
                                    </div>
                                    <p className="text-sm text-red-800">
                                        {operation.resultSummary.highRiskDrivers} driver{operation.resultSummary.highRiskDrivers > 1 ? 's' : ''} flagged as high risk.
                                        Review their status immediately.
                                    </p>
                                </div>
                            )}

                            {/* Risk Changes - show if available in resultSummary */}
                            {operation.resultSummary?.riskChanges > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                                        <p className="font-medium text-amber-800">Risk Level Changes</p>
                                    </div>
                                    <p className="text-sm text-amber-800">
                                        {operation.resultSummary.riskChanges} driver{operation.resultSummary.riskChanges > 1 ? 's have' : ' has'} had risk level changes detected.
                                    </p>
                                </div>
                            )}

                            {/* Execution Details */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                <h3 className="font-semibold text-gray-900 mb-3">Execution Details</h3>

                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Started</span>
                                    <span className="text-gray-900 font-medium">{formatTimestamp(operation.startedAt)}</span>
                                </div>

                                {operation.completedAt && (
                                    <>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Completed</span>
                                            <span className="text-gray-900 font-medium">{formatTimestamp(operation.completedAt)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Duration</span>
                                            <span className="text-gray-900 font-medium">{formatDuration(operation.startedAt, operation.completedAt)}</span>
                                        </div>
                                    </>
                                )}

                                {operation.status === 'running' && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Elapsed Time</span>
                                        <span className="text-gray-900 font-medium">{formatDuration(operation.startedAt)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Progress for Running Operations */}
                            {operation.status === 'running' && (
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Progress</span>
                                        <span className="text-gray-900 font-medium">
                                            {operation.progress} / {operation.totalItems} ({operation.progressPercentage}%)
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                            style={{
                                                width: `${operation.progressPercentage}%`
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkOperationModal;
